import React from 'react'
import classnames from 'classnames/bind'
import {
  InitialsAvatar,
  ExpandableCard,
  utils as coreUtils,
  CopyableField,
} from '@adc/ui-core'
import { getInitials } from '../closingOverview/helpers'
import type { Party, AssociatedPartiesProps } from './types'
import { processParties } from './helpers'
import styles from './styles.scss'

const { formatTitleCase } = coreUtils.formatters

const cx = classnames.bind(styles)
const MAX_INITIALS = 3

const PartyDetails: React.FC<{ party: Party }> = ({ party }) => {
  const roleText = party.displayLabel || formatTitleCase(party.role)

  return (
    <div className={cx('party-details')}>
      <InitialsAvatar
        initials={getInitials(party.name ?? '', MAX_INITIALS)}
        size='small'
        identifier={party.role!}
      />
      <div className={cx('party-info')}>
        <div className={cx('role')}>{roleText}</div>

        {party.personalName && (
          <div className={cx('name')}>
            <CopyableField
              value={party.personalName}
              showCopyButton='always'
              buttonSize='small'
              label='Name'
            />
          </div>
        )}

        {party.companyName && (
          <div className={cx('name')}>
            <CopyableField
              value={party.companyName}
              showCopyButton='always'
              buttonSize='small'
              label='Company'
            />
          </div>
        )}

        {party.contactName && (
          <div className={cx('info-item')}>
            <CopyableField
              value={party.contactName}
              showCopyButton='always'
              buttonSize='small'
              label='Contact'
            />
          </div>
        )}

        {party.contactEmail && (
          <div className={cx('info-item')}>
            <CopyableField
              value={party.contactEmail}
              showCopyButton='always'
              buttonSize='small'
              label='Contact Email'
            />
          </div>
        )}

        {party.contactPhone && (
          <div className={cx('info-item')}>
            <CopyableField
              value={party.contactPhone}
              showCopyButton='always'
              buttonSize='small'
              label='Contact Phone'
            />
          </div>
        )}

        {party.email_address && (
          <div className={cx('info-item')}>
            <CopyableField
              value={party.email_address}
              showCopyButton='always'
              buttonSize='small'
              label='Email'
            />
          </div>
        )}

        {party.phone_number && (
          <div className={cx('info-item')}>
            <CopyableField
              value={party.phone_number}
              showCopyButton='always'
              buttonSize='small'
              label='Phone'
            />
          </div>
        )}

        {party.license_number && (
          <div className={cx('info-item')}>
            <CopyableField
              value={`${party.license_number} (${
                party.license_state || 'N/A'
              })`}
              showCopyButton='always'
              buttonSize='small'
              label='License'
            />
          </div>
        )}

        {party.address && (
          <div className={cx('info-item', 'info-item-address')}>
            <CopyableField
              value={party.address}
              showCopyButton='always'
              buttonSize='small'
              label='Address'
            />
          </div>
        )}
      </div>
    </div>
  )
}

const AssociatedParties: React.FC<AssociatedPartiesProps> = ({
  parties = [],
  className,
  defaultExpanded = false,
  onExpand,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded)

  const formattedParties = processParties(parties)

  const handleToggle = () => {
    setIsExpanded(!isExpanded)
    onExpand?.(!isExpanded)
  }

  const headerContent = (
    <div className={cx('header-content')}>
      <h3 className={cx('title')}>Associated Parties</h3>
      <div className={cx('avatar-row')}>
        {formattedParties.map((party, index) => {
          return (
            <InitialsAvatar
              key={`associated-parties-${index}`}
              initials={getInitials(party.name ?? '', MAX_INITIALS)}
              size='small'
              identifier={party.role!}
              className={cx('avatar')}
            />
          )
        })}
      </div>
    </div>
  )

  const expandedContent = (
    <div className={cx('parties-container')}>
      {formattedParties.map((party, index) => (
        <PartyDetails key={`associated-parties-${index}`} party={party} />
      ))}
    </div>
  )

  return (
    <ExpandableCard
      elmId='associated-parties'
      active={isExpanded}
      onClick={handleToggle}
      slots={{
        header: () => headerContent,
        content: () => expandedContent,
      }}
      classNames={{
        card: cx('associated-parties-card', className),
      }}
      hideHeaderWhenExpanded={false}
      bottomMargin={0}
      expandedHeightMode='auto'
      heightHints={{
        collapsed: 90,
        expanded: 500,
      }}
    />
  )
}

export default AssociatedParties
